import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import {
  FileText,
  FileSpreadsheet,
  Printer,
  Download,
  Settings,
  Eye,
  Loader2
} from 'lucide-react';

const ExportsView = () => {
  const { currentProject } = useProject();
  const [selectedExport, setSelectedExport] = useState('stromlaufplan');
  const [isGenerating, setIsGenerating] = useState(false);
  const [labelConfig, setLabelConfig] = useState({
    tePerRow: '12',
    paperSize: 'A4',
    includeArtikelnummer: true,
    includeBezeichnung: true,
    includeNennstrom: true,
    fontSize: '8'
  });

  const exportTypes = [
    {
      id: 'stromlaufplan',
      name: 'Stromlaufplan PDF',
      description: 'Vollständiger allpoliger Stromlaufplan',
      icon: FileText,
      format: 'PDF'
    },
    {
      id: 'beschriftung',
      name: 'Beschriftungs-PDF',
      description: 'Druckbare Beschriftung für Automaten',
      icon: Printer,
      format: 'PDF'
    },
    {
      id: 'sicherungen',
      name: 'Sicherungsübersicht',
      description: 'Liste aller Sicherungen und Schutzgeräte',
      icon: FileSpreadsheet,
      format: 'Excel'
    },
    {
      id: 'leitungen',
      name: 'Leitungsübersicht',
      description: 'Liste aller Leitungen mit Spezifikationen',
      icon: FileSpreadsheet,
      format: 'Excel'
    },
    {
      id: 'klemmen',
      name: 'Klemmenübersicht',
      description: 'Liste aller Klemmen und Zuordnungen',
      icon: FileSpreadsheet,
      format: 'Excel'
    },
    {
      id: 'stueckliste',
      name: 'Stückliste',
      description: 'Materialliste mit Artikelnummern',
      icon: FileSpreadsheet,
      format: 'Excel'
    }
  ];

  const handleExport = async () => {
    setIsGenerating(true);
    // Simuliere Export-Generierung
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsGenerating(false);
    alert(`Export "${exportTypes.find(e => e.id === selectedExport)?.name}" wurde generiert (MOCK)`);
  };

  const handlePreview = () => {
    alert('Vorschau wird angezeigt (MOCK)');
  };

  return (
    <div className="h-full overflow-auto bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Exporte</CardTitle>
            <CardDescription>
              Exportieren Sie Ihr Projekt in verschiedenen Formaten
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {exportTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedExport(type.id)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedExport === type.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-6 h-6 ${
                        selectedExport === type.id ? 'text-emerald-600' : 'text-gray-400'
                      }`} />
                      <div>
                        <div className="font-medium text-sm">{type.name}</div>
                        <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                        <Badge variant="outline" className="mt-2 text-xs">
                          {type.format}
                        </Badge>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Beschriftungs-Konfiguration */}
        {selectedExport === 'beschriftung' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Beschriftungs-Einstellungen
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie die Beschriftungs-PDF für Ihre Automaten
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teilungseinheiten pro Reihe</Label>
                  <Select
                    value={labelConfig.tePerRow}
                    onValueChange={(value) => setLabelConfig(prev => ({ ...prev, tePerRow: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">12 TE (Standard)</SelectItem>
                      <SelectItem value="14">14 TE (Spelsberg AK)</SelectItem>
                      <SelectItem value="18">18 TE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Papierformat</Label>
                  <Select
                    value={labelConfig.paperSize}
                    onValueChange={(value) => setLabelConfig(prev => ({ ...prev, paperSize: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4</SelectItem>
                      <SelectItem value="A3">A3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Schriftgröße (pt)</Label>
                <Select
                  value={labelConfig.fontSize}
                  onValueChange={(value) => setLabelConfig(prev => ({ ...prev, fontSize: value }))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 pt</SelectItem>
                    <SelectItem value="7">7 pt</SelectItem>
                    <SelectItem value="8">8 pt</SelectItem>
                    <SelectItem value="9">9 pt</SelectItem>
                    <SelectItem value="10">10 pt</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label>Anzuzeigende Informationen</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="bezeichnung"
                      checked={labelConfig.includeBezeichnung}
                      onCheckedChange={(checked) => setLabelConfig(prev => ({ ...prev, includeBezeichnung: checked }))}
                    />
                    <label htmlFor="bezeichnung" className="text-sm">Bezeichnung</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="nennstrom"
                      checked={labelConfig.includeNennstrom}
                      onCheckedChange={(checked) => setLabelConfig(prev => ({ ...prev, includeNennstrom: checked }))}
                    />
                    <label htmlFor="nennstrom" className="text-sm">Nennstrom</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="artikelnummer"
                      checked={labelConfig.includeArtikelnummer}
                      onCheckedChange={(checked) => setLabelConfig(prev => ({ ...prev, includeArtikelnummer: checked }))}
                    />
                    <label htmlFor="artikelnummer" className="text-sm">Artikelnummer</label>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="border rounded-lg p-4 bg-white">
                <Label className="mb-3 block">Vorschau (Beispiel)</Label>
                <div className="flex gap-1 overflow-x-auto pb-2">
                  {Array.from({ length: parseInt(labelConfig.tePerRow) }).map((_, i) => (
                    <div
                      key={i}
                      className="flex-shrink-0 w-12 h-16 border border-gray-300 rounded text-center p-1 bg-gray-50"
                      style={{ fontSize: `${labelConfig.fontSize}px` }}
                    >
                      <div className="truncate font-medium">
                        {labelConfig.includeBezeichnung && `F1.${i + 1}`}
                      </div>
                      <div className="truncate text-gray-600">
                        {labelConfig.includeNennstrom && '16A'}
                      </div>
                      <div className="truncate text-gray-400">
                        {labelConfig.includeArtikelnummer && 'MBN116'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Buttons */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handlePreview}
              >
                <Eye className="w-4 h-4 mr-2" />
                Vorschau
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleExport}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {isGenerating ? 'Wird generiert...' : 'Exportieren'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ExportsView;
